import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Table from "components/Table/Table";
import { useCallback, useEffect, useMemo } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setIsLoading, setValidationState } from "reducers/main";
import {
  useDeleteRreTinMutation,
  useGetRreValidationsQuery,
  useGetTinsByRreIdQuery,
} from "services/rre-api";
import type { TinType } from "types/DataTypes";
import { useDispatch } from "utils/redux";

import RREPageContainer from "./RREPageContainer";

const RRETinsTable = () => {
  const dispatch = useDispatch();
  const { rreId } = useParams<{ rreId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [deleteRreTin] = useDeleteRreTinMutation();

  const { data, isLoading } = useGetTinsByRreIdQuery(
    { rreId },
    { skip: !rreId },
  );

  const { data: rreValidation } = useGetRreValidationsQuery(
    { id: rreId },
    { skip: !rreId },
  );

  useEffect(() => {
    if (rreValidation) {
      const validationData = Object.values(rreValidation).flat();
      const errors = validationData.filter((item) => item.level === "ERROR");
      const warnings = validationData.filter((item) => item.level === "WARN");
      dispatch(
        setValidationState({
          errors: errors,
          warnings: warnings,
        }),
      );
    }
  }, [dispatch, rreValidation]);

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const handleCellClick = (args: CellClickArgs<TinType, unknown>) => {
    navigate(`${location.pathname}/${args.row.id}/overview`);
  };

  const handleDeleteClick = useCallback(
    async (row) => {
      await deleteRreTin({ id: row.id });
    },
    [deleteRreTin],
  );

  const columns = useMemo(() => {
    return [
      { key: "tin", name: "TIN" },
      { key: "office_code", name: "Office Code" },
      { key: "tin_office_code_mailing_address_line1", name: "Address 1" },
      { key: "tin_office_code_mailing_address_line2", name: "Address 2" },
      { key: "tin_office_code_city", name: "City" },
      { key: "tin_office_code_state", name: "State" },
      { key: "tin_office_code_zipcode", name: "Zipcode" },
      {
        key: "actions",
        name: "Actions",
        width: "10%",
        renderCell(props) {
          return (
            <div className="action-column">
              <ButtonIcon
                className="btn-icon delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(props.row);
                }}
              >
                delete
              </ButtonIcon>
            </div>
          );
        },
      },
    ];
  }, []);

  return (
    <RREPageContainer>
      <Table
        className="rre-tins-table"
        columns={columns}
        rows={data?.results || []}
        rowKeyGetter={(row: TinType) => row.id}
        onCellClick={handleCellClick}
      />
    </RREPageContainer>
  );
};

export default RRETinsTable;
